import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Upload, FileText, CheckCircle2, Clock, Loader2, AlertCircle, CheckCircle, Trash2, Download, Presentation } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Card, CardContent, CardHeader, CardTitle, CardDescription,
  Badge, Separator, Tabs, TabsList, TabsTrigger, TabsContent,
} from '@higgins/ui';
import { useTemplates, useUploadTemplate, useSetTemplateActive, useDeleteTemplate } from '@/lib/hooks';
import { downloadTemplate } from '@/lib/api';
import type { ReportTemplate, TemplateKind } from '@/lib/types';
import { TEMPLATE_KIND_COVER, TEMPLATE_KIND_REPORT } from '@/lib/types';

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

const MOCK_TEMPLATES: ReportTemplate[] = [
  {
    id: '1', name: 'GM_Report_Cover_v1.docx', blobPath: '2024-01-15_GM_Report_Cover_v1.docx',
    isActive: true, uploadedOn: '2024-01-15T09:00:00Z', uploadedBy: 'D Davies', notes: 'Cover page', kind: TEMPLATE_KIND_COVER,
  },
  {
    id: '2', name: 'GM_Report_Template_v2.docx', blobPath: '2024-01-15_GM_Report_Template_v2.docx',
    isActive: true, uploadedOn: '2024-01-15T09:00:00Z', uploadedBy: 'D Davies', notes: 'Updated branding', kind: TEMPLATE_KIND_REPORT,
  },
  {
    id: '3', name: 'GM_Report_Template_v1.docx', blobPath: '2023-06-01_GM_Report_Template_v1.docx',
    isActive: false, uploadedOn: '2023-06-01T10:30:00Z', uploadedBy: 'D Davies', kind: TEMPLATE_KIND_REPORT,
  },
];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function TemplateRow({ template, onActivate, onDelete, onDownload, busy }: {
  template: ReportTemplate;
  onActivate: (id: string) => void;
  onDelete:   (id: string) => void;
  onDownload: (id: string) => void;
  busy: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3 gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{template.name}</p>
          <p className="text-xs text-muted-foreground">
            Uploaded {formatDate(template.uploadedOn)}
            {template.uploadedBy && ` by ${template.uploadedBy}`}
            {template.notes && ` · ${template.notes}`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {template.isActive ? (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Active
          </Badge>
        ) : (
          <>
            <Badge variant="secondary">
              <Clock className="w-3 h-3 mr-1" />
              Superseded
            </Badge>
            <Button
              variant="ghost" size="sm" className="h-7 px-2"
              onClick={() => onActivate(template.id)}
              disabled={busy}
              title="Make this template active"
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" />
              Activate
            </Button>
          </>
        )}
        <Button
          variant="ghost" size="sm" className="h-7 px-2"
          onClick={() => onDownload(template.id)}
          disabled={busy}
          title="Download this template"
        >
          <Download className="w-3.5 h-3.5" />
        </Button>
        {!template.isActive && (
          <Button
            variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:text-destructive"
            onClick={() => onDelete(template.id)}
            disabled={busy}
            title="Delete this template permanently"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

/** Upload zone + history for a single template kind (Cover or Report). */
function TemplateSection({ kind, hint, templates, isLoading, error, uploading, busy, onUpload, onActivate, onDelete, onDownload }: {
  kind: TemplateKind;
  hint: React.ReactNode;
  templates: ReportTemplate[];
  isLoading: boolean;
  error: boolean;
  uploading: boolean;
  busy: boolean;
  onUpload:   (file: File, kind: TemplateKind) => void;
  onActivate: (id: string) => void;
  onDelete:   (id: string) => void;
  onDownload: (id: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFile(file: File | null | undefined) {
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      toast.error('Invalid file type', { description: 'Only .docx templates are accepted.' });
      return;
    }
    onUpload(file, kind);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload new template</CardTitle>
          <CardDescription>{hint}</CardDescription>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            className="hidden"
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          />
          <div
            className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
              dragOver ? 'border-primary bg-accent' : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          >
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-sm text-muted-foreground">Uploading…</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-sm font-medium">Drop a .docx file here or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">The uploaded template becomes active immediately</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Template history</CardTitle>
          <CardDescription>All uploaded templates, newest first.</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading && (
            <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 py-4 text-destructive text-sm">
              <AlertCircle className="w-4 h-4" />
              Failed to load templates
            </div>
          )}
          {!isLoading && templates.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">No templates uploaded yet.</p>
          )}
          {!isLoading && templates.map((t, i) => (
            <div key={t.id}>
              {i > 0 && <Separator />}
              <TemplateRow
                template={t}
                onActivate={onActivate}
                onDelete={onDelete}
                onDownload={onDownload}
                busy={busy}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminTemplatesPage() {
  const { data: templates = IS_MOCK ? MOCK_TEMPLATES : [], isLoading, error } = useTemplates();
  const uploadMutation   = useUploadTemplate();
  const activateMutation = useSetTemplateActive();
  const deleteMutation   = useDeleteTemplate();

  const coverTemplates  = templates.filter(t => t.kind === TEMPLATE_KIND_COVER);
  const reportTemplates = templates.filter(t => t.kind === TEMPLATE_KIND_REPORT);

  async function handleUpload(file: File, kind: TemplateKind) {
    if (IS_MOCK) { toast.success('Template uploaded (mock)', { description: file.name }); return; }
    try {
      await uploadMutation.mutateAsync({ file, kind });
      toast.success('Template uploaded', { description: `${file.name} is now the active template.` });
    } catch (e: any) {
      toast.error('Upload failed', { description: e?.message });
    }
  }

  async function handleActivate(id: string) {
    if (IS_MOCK) { toast.info('Activate (mock)', { description: 'Not available in demo mode.' }); return; }
    try {
      await activateMutation.mutateAsync({ id, isActive: true });
      toast.success('Template activated');
    } catch (e: any) {
      toast.error('Activate failed', { description: e?.message });
    }
  }

  async function handleDelete(id: string) {
    if (IS_MOCK) { toast.info('Delete (mock)', { description: 'Not available in demo mode.' }); return; }
    if (!window.confirm('Delete this template permanently? This cannot be undone.')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Template deleted');
    } catch (e: any) {
      toast.error('Delete failed', { description: e?.message });
    }
  }

  async function handleDownload(id: string) {
    if (IS_MOCK) { toast.info('Download (mock)', { description: 'Not available in demo mode.' }); return; }
    try {
      const { blob, filename } = await downloadTemplate(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error('Download failed', { description: e?.message });
    }
  }

  const busy = activateMutation.isPending || deleteMutation.isPending;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl space-y-6"
    >
      <div>
        <h2 className="text-xl font-bold text-slate-900">Word Templates</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Every Word export is a <strong>cover page</strong> followed by the <strong>report body</strong>.
          Each is managed separately below. Uploading a new template immediately supersedes the previous one.
        </p>
      </div>

      <Tabs defaultValue="report">
        <TabsList>
          <TabsTrigger value="cover">Cover Page</TabsTrigger>
          <TabsTrigger value="report">Report Body</TabsTrigger>
          <TabsTrigger value="pptx">PowerPoint</TabsTrigger>
        </TabsList>

        <TabsContent value="cover" className="mt-6">
          <TemplateSection
            kind={TEMPLATE_KIND_COVER}
            hint={<>
              The cover page shown at the front of every report (and once at the front of a combined report).
              Use the <code className="text-xs bg-muted px-1 rounded">{'{{MONTH–YEAR}}'}</code> placeholder where
              the report month should appear — it is filled in automatically at export time.
            </>}
            templates={coverTemplates}
            isLoading={isLoading}
            error={!!error}
            uploading={uploadMutation.isPending}
            busy={busy}
            onUpload={handleUpload}
            onActivate={handleActivate}
            onDelete={handleDelete}
            onDownload={handleDownload}
          />
        </TabsContent>

        <TabsContent value="report" className="mt-6">
          <TemplateSection
            kind={TEMPLATE_KIND_REPORT}
            hint={<>
              The report body. Prepare it with{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{{MONTH-YEAR}}'}</code>,{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{{REGION}}'}</code>,{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{{branches}}'}</code>, and{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{{PREPARED_BY}}'}</code> placeholders.
              Leave visual table cells empty for chart injection.
            </>}
            templates={reportTemplates}
            isLoading={isLoading}
            error={!!error}
            uploading={uploadMutation.isPending}
            busy={busy}
            onUpload={handleUpload}
            onActivate={handleActivate}
            onDelete={handleDelete}
            onDownload={handleDownload}
          />
        </TabsContent>

        <TabsContent value="pptx" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Presentation className="w-4 h-4" />
                PowerPoint templates
              </CardTitle>
              <CardDescription>Not yet implemented.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                PowerPoint decks are currently generated entirely from code — there is no uploadable
                PowerPoint template. Template-based PPTX styling is tracked as an enhancement
                (issue&nbsp;#63) and will appear here once implemented.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
